import { useState, useEffect } from 'react';

/* Shattered glass shard configurations - polygon clip-paths */
const shards = [
  { clipPath: "polygon(0 0, 35% 0, 20% 45%, 0 25%)", offset: { x: -8, y: -6, r: -2 } },
  { clipPath: "polygon(35% 0, 55% 0, 45% 40%, 20% 45%)", offset: { x: 5, y: -10, r: 3 } },
  { clipPath: "polygon(55% 0, 80% 0, 70% 35%, 45% 40%)", offset: { x: -4, y: 6, r: -1 } },
  { clipPath: "polygon(80% 0, 100% 0, 100% 30%, 70% 35%)", offset: { x: 7, y: -4, r: 2 } },
  { clipPath: "polygon(0 25%, 20% 45%, 25% 70%, 0 60%)", offset: { x: 6, y: 5, r: 1 } },
  { clipPath: "polygon(20% 45%, 45% 40%, 55% 75%, 25% 70%)", offset: { x: -7, y: -5, r: -3 } },
  { clipPath: "polygon(45% 40%, 70% 35%, 75% 70%, 55% 75%)", offset: { x: 8, y: 4, r: 2 } },
  { clipPath: "polygon(70% 35%, 100% 30%, 100% 65%, 75% 70%)", offset: { x: -5, y: -7, r: -1 } },
  { clipPath: "polygon(0 60%, 25% 70%, 30% 100%, 0 100%)", offset: { x: -3, y: 8, r: 3 } },
  { clipPath: "polygon(25% 70%, 55% 75%, 50% 100%, 30% 100%)", offset: { x: 5, y: -6, r: -2 } },
  { clipPath: "polygon(55% 75%, 75% 70%, 80% 100%, 50% 100%)", offset: { x: -6, y: 7, r: 1 } },
  { clipPath: "polygon(75% 70%, 100% 65%, 100% 100%, 80% 100%)", offset: { x: 4, y: -5, r: -3 } },
];

const bgUrl =
  "https://readdy.ai/api/search-image?query=smashed%20vintage%20CRT%20television%20wrapped%20with%20crimson%20red%20tape%20bandages%20in%20dark%20concrete%20room%2C%20static%20noise%20on%20the%20screen%2C%20shattered%20glass%20debris%20scattered%20on%20dusty%20floor%2C%20dramatic%20low-key%20cinematic%20lighting%20with%20deep%20red%20accent%20highlights%2C%20dark%20grunge%20decay%20atmosphere%2C%20raw%20aggressive%20editorial%20photography%2C%20no%20text%20no%20typography%20no%20logos&width=1600&height=900&seq=hero-broken-tv-v1&orientation=landscape&nocache=true";

export default function Hero() {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className="relative min-h-screen w-full bg-black overflow-hidden pt-16 md:pt-20"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Base full image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
        style={{
          backgroundImage: `url(${bgUrl})`,
          filter: hovered ? "brightness(0.45)" : "brightness(0.32)",
        }}
      />

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/60" />

      {/* Shattered glass shards */}
      <div className="absolute inset-0">
        {shards.map((shard, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-transform duration-500 ease-out"
            style={{
              clipPath: shard.clipPath,
              backgroundImage: `url(${bgUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: mounted
                ? hovered
                  ? `translate(${shard.offset.x * 2.5}px, ${shard.offset.y * 2.5}px) rotate(${shard.offset.r * 2.5}deg) scale(1.02)`
                  : `translate(${shard.offset.x}px, ${shard.offset.y}px) rotate(${shard.offset.r}deg)`
                : `translate(0, 0) rotate(0deg)`,
              transitionDelay: `${i * 30}ms`,
              filter: "brightness(0.55)",
              opacity: mounted ? 1 : 0,
              transition: `transform 500ms ease-out ${i * 30}ms, opacity 800ms ease-out ${i * 50}ms`,
            }}
          />
        ))}
      </div>

      {/* Crack lines overlay */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-25"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        <line x1="20" y1="45" x2="55" y2="40" stroke="white" strokeWidth="0.15" />
        <line x1="45" y1="40" x2="70" y2="35" stroke="white" strokeWidth="0.15" />
        <line x1="55" y1="75" x2="75" y2="70" stroke="white" strokeWidth="0.15" />
        <line x1="25" y1="70" x2="55" y2="75" stroke="white" strokeWidth="0.15" />
        <line x1="0" y1="25" x2="20" y2="45" stroke="white" strokeWidth="0.15" />
        <line x1="80" y1="0" x2="70" y2="35" stroke="white" strokeWidth="0.15" />
        <line x1="70" y1="35" x2="100" y2="30" stroke="white" strokeWidth="0.15" />
        <line x1="75" y1="70" x2="100" y2="65" stroke="white" strokeWidth="0.15" />
        <line x1="0" y1="60" x2="25" y2="70" stroke="white" strokeWidth="0.15" />
      </svg>

      {/* Red radial glow behind title */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full pointer-events-none z-10"
        style={{
          background: "radial-gradient(ellipse, rgba(244,0,9,0.10) 0%, rgba(244,0,9,0.03) 50%, transparent 70%)",
        }}
      />

      {/* Giant center text */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-[calc(100vh-5rem)] px-4">
        <div className="text-center">
          {/* Title with staggered animation */}
          <h2
            className={`font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white tracking-tighter leading-[0.9] transition-all duration-1000 ease-out ${
              mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
            }`}
            style={{
              textShadow: "0 0 60px rgba(244,0,9,0.4), 0 4px 20px rgba(0,0,0,0.9), 0 0 120px rgba(244,0,9,0.2)",
              transitionDelay: '200ms',
            }}
          >
            YARATICI
            <br />
            YIKIM
          </h2>

          {/* CTA Buttons with staggered animation */}
          <div
            className={`mt-8 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 transition-all duration-700 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 bg-primary-500 text-foreground-50 rounded-full px-6 py-3 md:px-10 md:py-4 text-sm md:text-base font-bold tracking-widest uppercase hover:bg-primary-600 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
            >
              HADİ YIKALIM
              <i className="ri-arrow-right-line text-base md:text-lg group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#cases"
              className="group inline-flex items-center gap-2 text-white border-2 border-white/30 rounded-full px-6 py-3 md:px-10 md:py-4 text-sm md:text-base font-bold tracking-widest uppercase hover:bg-white/10 hover:border-white/50 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
            >
              SONUÇLARA BAK
              <i className="ri-arrow-right-line text-base md:text-lg group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}